import { useDispatch, useSelector } from 'redhotpie-shared-lib/hooks/useRedux';
import I18n from 'hooks/useI18n';
import moment from 'moment';
import * as firebase from 'lib/firebase/firebaseConfig';

import {
  toggleNotificationGroupActivity,
  toggleNotificationGroupJoinRequest,
  toggleNotificationGroupJoins,
  toggleNotificationInvitation,
  updateCommonStateReducerSuccess,
} from 'redhotpie-shared-lib/actions/commonStateAction';
import {
  ClubMediaParentType,
  EVENT_TRACKING,
  PAYMENT_GATEWAY,
  SCORE_STAR_DEFAULT,
  STYLE_MODAL,
  TABS_MENU,
} from 'redhotpie-shared-lib/constants/constants';
import { getPaymentOptions } from 'redhotpie-shared-lib/selectors/paymentSelector';
import { gotoLinkRHP, handleShowConfirmModal, phoneDetect } from 'redhotpie-shared-lib/functions/utils';
import { useCallback } from 'react';
import { updateMemberProfileState } from 'redhotpie-shared-lib/actions/memberProfileActions';
import StarRate from 'modules/StarRate';
import { useRouter } from 'hooks/useRouter';
import urls from 'redhotpie-shared-lib/api/urls';
import { EVENT_DETAIL, EVENTS, GROUP_ALBUM_DETAIL_VIEW, GROUP_POST_DETAIL_VIEW, ROOT_FOLDER } from 'redhotpie-shared-lib/constants/routes';
import groupService from 'redhotpie-shared-lib/services/groupService';
import { stopEvent } from './webUtils';
import { mergeDataEvent } from 'redhotpie-shared-lib/actions/eventActions';

const useSelectorWebUtils = () => {
  const dispatch = useDispatch();
  const { history } = useRouter();
  const isMobile = phoneDetect();

  const paymentOptions = useSelector(getPaymentOptions);

  function handleOpenRHPCoins(needMoreCoins = 0, callback) {
    dispatch(
      updateCommonStateReducerSuccess({
        isShowRHPCoins: true,
        needMoreCoins,
        callbackAfterGetMoreCoinsSuccess: callback,
      })
    );
  }

  const isOutsideAU = useCallback(() => {
    return paymentOptions.findIndex(itm => itm === PAYMENT_GATEWAY.TYPE.COM_WEB) === -1;
    // return userInfo?.country?.name !== REFINE_MODAL.LOCATION_DEFAULT || false;
  }, [paymentOptions]);

  const generateAge = birthDate => {
    const age = moment().diff(birthDate, 'years');
    return `${age} yo`;
  };

  const generateAgeOnly = birthDate => {
    return moment().diff(birthDate, 'years');
  };

  const handleShowChatRoomGuidelines = () => {
    handleShowConfirmModal({
      headerText: I18n.t('ChatRoomsScreen.ChatroomGuidelines'),
      messageConfirm: (
        <div className="cr-gl-content">
          <div className="cr-gl-des">
            <span className="_tit">{I18n.t('ChatRoomsScreen.ChatroomGuidelinesDes1Title')}</span>
            <span className="_des">{I18n.t('ChatRoomsScreen.ChatroomGuidelinesDes1')}</span>
          </div>
          <div className="cr-gl-des">
            <span className="_tit">{I18n.t('ChatRoomsScreen.ChatroomGuidelinesDes2Title')}</span>
            <span className="_des">{I18n.t('ChatRoomsScreen.ChatroomGuidelinesDes2')}</span>
          </div>
          <div className="cr-gl-des">
            <span className="_tit">{I18n.t('ChatRoomsScreen.ChatroomGuidelinesDes3Title')}</span>
            <span className="_des">{I18n.t('ChatRoomsScreen.ChatroomGuidelinesDes3')}</span>
          </div>
          <div className="cr-gl-des">
            <span className="_tit">{I18n.t('ChatRoomsScreen.ChatroomGuidelinesDes4')}</span>
          </div>
        </div>
      ),
      okText: I18n.t('Btn.OkGotIt'),
      disableCancel: true,
      className: `${STYLE_MODAL.BLOCK_MODAL} cr-gl-wrapper`,
    });
  };

  const handleShowLocationSearchModal = ({
    onActionClick,
    isEvent = false,
    isClubOrganiser = false,
    location = {},
    searchType = '',
    isSuburbOnly,
  }) => {
    dispatch(
      updateCommonStateReducerSuccess({
        locationSearchInfo: {
          isShowLocationSearchModal: true,
          onCancelClick: null,
          onActionClick,
          isEvent,
          isClubOrganiser,
          location,
          searchType,
          isSuburbOnly,
        },
      })
    );
  };

  const handleShowReviewGroupModal = (rateScore, callbackSubmit, callbackSubmitScore, callbackCheckAfterSubmit) => {
    const callback = score => {
      callbackSubmitScore(score);
    };
    dispatch(
      updateMemberProfileState({
        textInput: {
          headerTitle: I18n.t('ClubDetail.WriteAReview'),
          isShowTextInput: true,
          onSubmitClick: review => callbackSubmit(review),
          checkAfterSubmit: review => callbackCheckAfterSubmit(review),
          onCancleClick: handleCloseReview,
          data: null,
          placeHolder: I18n.t('ClubDetail.WriteAReview'),
          otherContent: (
            <>
              <div className="rate-title">{I18n.t('ClubDetail.Rating')}</div>
              <div className="rate-option">
                <StarRate score={rateScore || 0} callback={callback} />
                <span>{I18n.t('ClubDetail.TabAStarToRate')}</span>
              </div>
              <div className="rate-title review">{I18n.t('ClubDetail.Review')}</div>
            </>
          ),
          textButton: I18n.t('Btn.Post'),
        },
      })
    );
  };

  function handleCloseReview() {
    dispatch(
      updateMemberProfileState({
        textInput: {
          headerTitle: '',
          isShowTextInput: false,
          onSubmitClick: null,
          onCancleClick: null,
          type: null,
          data: '',
          infoWantReport: '',
          otherContent: undefined,
        },
      })
    );
  }

  function handleClickPrivacyPolicy() {
    gotoLinkRHP(urls.PRIVACY_POLICY, null, '_blank');
  }

  function addDataIntoSearchQuery(data) {
    const searchParams = new URL(window.location.href).searchParams;
    Object.keys(data).forEach(key => {
      searchParams.set(key, data[key]);
    });
    return searchParams;
  }

  async function redirectToGroupPostAlbumDetail(id, contentType, type) {
    switch (contentType) {
      case 'ClubPost':
        history.push(`${ROOT_FOLDER}/${GROUP_POST_DETAIL_VIEW}?id=${id}&isO=true`);
        break;
      case 'ClubAlbum':
        history.push(`${ROOT_FOLDER}/${GROUP_ALBUM_DETAIL_VIEW}?id=${id}&isO=true`);
        break;
      case 'ClubMedia':
        const resMedia = await groupService.getClubMedia(id, true);
        if (resMedia.success && resMedia.data.length > 0) {
          const mediaData = resMedia.data[0];
          if (mediaData.parentType === ClubMediaParentType.Album) {
            history.push(`${ROOT_FOLDER}/${GROUP_ALBUM_DETAIL_VIEW}?id=${mediaData.parentId}&isO=true`);
          } else if (mediaData.parentType === ClubMediaParentType.Post) {
            history.push(`${ROOT_FOLDER}/${GROUP_POST_DETAIL_VIEW}?id=${mediaData.parentId}&isO=true`);
          }
        }
        break;
      default:
        break;
    }
  }

  function handleShowGroupJoinRequest() {
    dispatch(toggleNotificationGroupJoinRequest(true));
  }

  function handleShowInvitation(type) {
    dispatch(toggleNotificationInvitation(type, true));
  }

  function handleShowGroupActivities() {
    dispatch(toggleNotificationGroupActivity(true));
  }

  function handleShowGroupJoins() {
    dispatch(toggleNotificationGroupJoins(true));
  }

  function handleOpenEventDetail(event, eventId, tabDefault = TABS_MENU.EVENT_DETAIL[0].id) {
    stopEvent(event);
    let url = '';
    if (isMobile) {
      url = `${ROOT_FOLDER}/${EVENT_DETAIL}?id=${eventId}`;
    } else {
      // url = `${ROOT_FOLDER}/${EVENTS}`;
      dispatch(
        mergeDataEvent({
          idEventSelected: eventId,
          typeId: tabDefault,
        })
      );
    }

    if (url) {
      history.push(url);
    }
    firebase.logsEvent(EVENT_TRACKING.HOME_EVENT_OPEN);
  }

  function getHasGroupChatAccess(groupChatDetail, userId) {
    const userLeaveGroupChat =
      groupChatDetail &&
      groupChatDetail.inActiveRoomMembers &&
      groupChatDetail.inActiveRoomMembers.find(i => i.userId === userId && !i.isActive && i.isLeft);
    const userIsPendingConfirmation =
      groupChatDetail &&
      groupChatDetail.pendingRoomMembers &&
      groupChatDetail.pendingRoomMembers.find(i => i.userId === userId && i.isPendingConfirmation);
    const userIsMember =
      groupChatDetail &&
      groupChatDetail.roomMembers &&
      groupChatDetail.roomMembers.find(i => i.userId === userId && i.isActive && !i.isLeft && !i.isPendingConfirmation);

    return { userLeaveGroupChat, userIsPendingConfirmation, userIsMember };
  }

  return {
    handleOpenRHPCoins,
    isOutsideAU,
    generateAge,
    generateAgeOnly,
    handleShowChatRoomGuidelines,
    handleShowLocationSearchModal,
    handleShowReviewGroupModal,
    handleClickPrivacyPolicy,
    addDataIntoSearchQuery,
    redirectToGroupPostAlbumDetail,
    handleShowGroupJoinRequest,
    handleShowGroupActivities,
    handleShowInvitation,
    handleOpenEventDetail,
    getHasGroupChatAccess,
    handleShowGroupJoins,
  };
};

export default useSelectorWebUtils;
